import { useEffect, useRef, useState } from 'react'
import { StarTooltip } from '../tooltips/Tooltip'

/**
 * Input customizzabile
 *
 * @param {string} label - label che appare sopra la select,
 * @param {string} type - Il tipo del campo di input.
 * @param {ReactElement} Icon - Il componente icona visualizzato accanto al campo di input.
 * @param {Function} onIconClick - La funzione che viene chiamata quando l'icona viene cliccata.
 * @param {boolean} clickableIcon - Se vero, l'icona è cliccabile.
 * @param {string} iconTip - Il testo del tooltip per l'icona.
 * @param {string} placeholder - Il testo segnaposto per il campo di input.
 * @param {string} formClassName - Il nome della classe per il gruppo di form.
 * @param {string} className - Il nome della classe per il campo di input.
 * @param {Function} onChange - La funzione che viene chiamata quando il campo di input viene modificato.
 * @param {string} name - Il nome del campo di input.
 * @param {any} value - Il valore del campo di input.
 * @param {Function} onKeyPress - La funzione che viene chiamata quando viene premuto il tasto Invio.
 * @param {Function} onFocus - La funzione che viene chiamata quando il campo di input viene messo a fuoco.
 * @param {number} maxLength - La lunghezza massima del testo di input.
 * @param {object} error - messaggio di errore
 * @param {string} pattern - Il pattern utilizzato per convalidare il testo di input.
 * @param {number} step - L'incremento del testo di input.
 * @param {Function} onBlur La funzione che viene chiamata quando il campo di input perde il fuoco.
 * @param {boolean} readOnly Se vero, il campo di input è di sola lettura.
 * @param {string} id L'id del campo di input.
 * @param {Function} onInput La funzione che viene chiamata quando il campo di input viene modificato.
 * @param {boolean} disabled Se vero, il campo di input è disabilitato.
 * @param {string} autoComplete Il valore dell'attributo autoComplete.
 * @param {boolean} autofocus Se vero, il campo di input viene messo a fuoco quando il componente viene montato.
 */
export const BwmInput = ({
  label,
  type,
  Icon,
  onIconClick = () => undefined,
  clickableIcon = true,
  iconTip,
  placeholder,
  formClassName,
  className,
  onChange,
  name,
  value,
  onKeyPress,
  onFocus,
  maxLength,
  error,
  pattern,
  step,
  onBlur = () => {},
  readOnly,
  id,
  onInput,
  disabled,
  autoComplete,
  autofocus = false
}) => {
  /**
   * in certi casi, il cursore può saltare alla fine. questo stato riporta il cursore al posto giusto
   */
  const [cursor, setCursor] = useState(null)
  const ref = useRef(null)

  /**
   * Focus su primo render, se richiesto
   */
  useEffect(() => {
    if (autofocus) {
      ref.current.focus()
    }
  }, [])

  /**
   * in certi casi, il cursore può saltare alla fine. questo stato riporta il cursore al posto giusto
   */
  useEffect(() => {
    const input = ref.current
    if (input && ['text', 'search', 'URL', 'tel', 'password'].includes(input.type))
      input.setSelectionRange(cursor || 0, cursor || 0)
  }, [ref, cursor, value])

  /**
   * Chiamato quando il campo di input cambia.
   * @param {React.ChangeEvent<HTMLInputElement>} e - L'evento
   * @private
   */
  const handleChange = (e) => {
    setCursor(e.target.selectionStart)
    onChange && onChange(e)
  }

  return (
    <div className={`form-group ${formClassName || ''}`}>
      {label && <label>{label}</label>}
      <div className="input-group">
        <input
          ref={ref}
          id={id}
          type={type}
          className={className}
          name={name}
          value={![null, undefined, ''].includes(value) ? value : ''}
          placeholder={placeholder}
          maxLength={maxLength}
          pattern={pattern}
          step={step}
          readOnly={readOnly}
          onInput={onInput}
          disabled={disabled}
          autoComplete={autoComplete}
          onKeyDown={onKeyPress}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={handleChange}
        />

        {Icon && Icon() && (
          <div className="input-group-append">
            <StarTooltip text={iconTip}>
              <div
                className={`${className} input-group-text py-0}`}
                onClick={onIconClick}
                style={{
                  cursor: clickableIcon && 'pointer',
                  border: 0,
                  background: 'inherit',
                  color: 'inherit'
                }}>
                <Icon />
              </div>
            </StarTooltip>
          </div>
        )}
      </div>
      {error !== undefined && !error.isValid && <div className="text-danger">{error.msg}</div>}
    </div>
  )
}
